"use client"

import * as React from "react"

function formatNumber(num: number): string {
  if (isNaN(num) || !isFinite(num)) return "Erro"
  const precisionFix = Number(num.toPrecision(12))
  return parseFloat(precisionFix.toString()).toString()
}

function calculate(a: number, b: number, op: string): number {
  switch (op) {
    case "+":
      return a + b
    case "-":
      return a - b
    case "*":
    case "×":
      return a * b
    case "/":
    case "÷":
      if (b === 0) return NaN
      return a / b
    default:
      return b
  }
}

function formatOp(op: string): string {
  if (op === "*") return "×"
  if (op === "/") return "÷"
  return op
}

export function useCalculatorState() {
  const [calcDisplay, setCalcDisplay] = React.useState("0")
  const [calcFormula, setCalcFormula] = React.useState("")
  const [calcResetOnNext, setCalcResetOnNext] = React.useState(false)
  const [calcHistory, setCalcHistory] = React.useState<{ expr: string; result: string }[]>([])
  const [showCalcHistory, setShowCalcHistory] = React.useState(false)
  const [calcFocusedWidgetId, setCalcFocusedWidgetId] = React.useState<string | null>(null)

  // Internal calculator state machine refs
  const prevValueRef = React.useRef<number | null>(null)
  const operatorRef = React.useRef<string | null>(null)
  const waitingForOperandRef = React.useRef<boolean>(false)
  const lastCompletedRef = React.useRef<boolean>(false)

  const handleCalcPress = React.useCallback((rawVal: string) => {
    const val = rawVal.trim()

    // 1. ALL CLEAR / CLEAR (AC / C)
    if (val === "AC" || val === "C") {
      setCalcDisplay("0")
      setCalcFormula("")
      prevValueRef.current = null
      operatorRef.current = null
      waitingForOperandRef.current = false
      lastCompletedRef.current = false
      setCalcResetOnNext(false)
      return
    }

    // 2. BACKSPACE / DELETE (del)
    if (val === "del" || val === "Backspace" || val === "⌫") {
      if (waitingForOperandRef.current || lastCompletedRef.current) return

      setCalcDisplay((prev) => {
        if (prev === "Erro" || prev.length <= 1) return "0"
        const next = prev.slice(0, -1)
        return next === "-" || next === "" ? "0" : next
      })
      return
    }

    // 3. PLUS / MINUS SIGN TOGGLE (± or +/-)
    if (val === "±" || val === "+/-") {
      setCalcDisplay((prev) => {
        if (prev === "0" || prev === "Erro") return prev
        if (prev.startsWith("-")) return prev.slice(1)
        return "-" + prev
      })
      return
    }

    // 4. PERCENTAGE (%)
    if (val === "%") {
      setCalcDisplay((prev) => {
        if (prev === "Erro") return prev
        const current = parseFloat(prev.replace(",", "."))
        if (isNaN(current)) return "0"

        if (operatorRef.current && prevValueRef.current !== null) {
          // Ex: 100 + 10% = 10 (10% de 100)
          const percentVal = (prevValueRef.current * current) / 100
          return formatNumber(percentVal)
        }

        return formatNumber(current / 100)
      })
      return
    }

    // 5. DECIMAL SEPARATOR (. or ,)
    if (val === "." || val === ",") {
      if (lastCompletedRef.current) {
        setCalcDisplay("0.")
        setCalcFormula("")
        prevValueRef.current = null
        operatorRef.current = null
        waitingForOperandRef.current = false
        lastCompletedRef.current = false
        return
      }

      if (waitingForOperandRef.current) {
        setCalcDisplay("0.")
        waitingForOperandRef.current = false
        return
      }

      setCalcDisplay((prev) => {
        if (prev === "Erro") return "0."
        if (!prev.includes(".")) {
          return prev + "."
        }
        return prev
      })
      return
    }

    // 6. NUMERIC DIGITS (0 to 9)
    if (/^[0-9]$/.test(val)) {
      if (lastCompletedRef.current) {
        setCalcDisplay(val)
        setCalcFormula("")
        prevValueRef.current = null
        operatorRef.current = null
        waitingForOperandRef.current = false
        lastCompletedRef.current = false
        return
      }

      if (waitingForOperandRef.current) {
        setCalcDisplay(val)
        waitingForOperandRef.current = false
        return
      }

      setCalcDisplay((prev) => {
        if (prev === "0" || prev === "Erro") {
          return val
        }
        if (prev === "-0") {
          return "-" + val
        }
        if (prev.replace("-", "").replace(".", "").length >= 15) return prev
        return prev + val
      })
      return
    }

    // 7. OPERATORS (+, -, *, /, ×, ÷, –)
    const isOp = ["+", "-", "*", "/", "×", "÷", "–"].includes(val)
    if (isOp) {
      const normalizedOp = val === "×" ? "*" : val === "÷" ? "/" : val === "–" ? "-" : val
      const currentVal = parseFloat(calcDisplay.replace(",", "."))

      if (lastCompletedRef.current) {
        prevValueRef.current = currentVal
        operatorRef.current = normalizedOp
        waitingForOperandRef.current = true
        lastCompletedRef.current = false
        setCalcFormula(`${formatNumber(currentVal)} ${formatOp(normalizedOp)}`)
        return
      }

      if (operatorRef.current && waitingForOperandRef.current) {
        // Apenas substitui o operador atual
        operatorRef.current = normalizedOp
        if (prevValueRef.current !== null) {
          setCalcFormula(`${formatNumber(prevValueRef.current)} ${formatOp(normalizedOp)}`)
        }
        return
      }

      if (prevValueRef.current === null) {
        prevValueRef.current = currentVal
        operatorRef.current = normalizedOp
        waitingForOperandRef.current = true
        setCalcFormula(`${formatNumber(currentVal)} ${formatOp(normalizedOp)}`)
        return
      }

      if (operatorRef.current !== null && prevValueRef.current !== null) {
        // Sequenciamento de operações (ex: 5 + 3, e o usuário pressiona + novamente)
        const result = calculate(prevValueRef.current, currentVal, operatorRef.current)

        if (isNaN(result) || !isFinite(result)) {
          setCalcDisplay("Erro")
          setCalcFormula("")
          prevValueRef.current = null
          operatorRef.current = null
          waitingForOperandRef.current = true
          lastCompletedRef.current = true
          return
        }

        const formattedResult = formatNumber(result)
        setCalcDisplay(formattedResult)
        prevValueRef.current = result
        operatorRef.current = normalizedOp
        waitingForOperandRef.current = true
        setCalcFormula(`${formattedResult} ${formatOp(normalizedOp)}`)
        return
      }
    }

    // 8. EQUALS (= or Enter)
    if (val === "=" || val === "Enter") {
      if (operatorRef.current === null || prevValueRef.current === null) {
        if (!lastCompletedRef.current && calcFormula === "") {
          const current = parseFloat(calcDisplay.replace(",", "."))
          if (!isNaN(current)) {
            setCalcFormula(`${formatNumber(current)} =`)
          }
        }
        waitingForOperandRef.current = true
        lastCompletedRef.current = true
        return
      }

      const currentVal = parseFloat(calcDisplay.replace(",", "."))
      const result = calculate(prevValueRef.current, currentVal, operatorRef.current)
      const exprStr = `${formatNumber(prevValueRef.current)} ${formatOp(operatorRef.current)} ${formatNumber(currentVal)}`

      if (isNaN(result) || !isFinite(result)) {
        setCalcDisplay("Erro")
        setCalcFormula(`${exprStr} =`)
        prevValueRef.current = null
        operatorRef.current = null
        waitingForOperandRef.current = true
        lastCompletedRef.current = true
        return
      }

      const formattedResult = formatNumber(result)
      setCalcDisplay(formattedResult)
      setCalcFormula(`${exprStr} =`)
      setCalcHistory((prev) => [{ expr: exprStr, result: formattedResult }, ...prev].slice(0, 50))

      prevValueRef.current = null
      operatorRef.current = null
      waitingForOperandRef.current = true
      lastCompletedRef.current = true
    }
  }, [calcDisplay, calcFormula])

  // Suporte global a teclado
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return
      }

      const key = e.key
      if (/^[0-9]$/.test(key)) {
        handleCalcPress(key)
      } else if (key === "+" || key === "-" || key === "*" || key === "/") {
        e.preventDefault()
        handleCalcPress(key)
      } else if (key === "." || key === ",") {
        e.preventDefault()
        handleCalcPress(".")
      } else if (key === "Enter" || key === "=") {
        e.preventDefault()
        handleCalcPress("=")
      } else if (key === "Backspace") {
        handleCalcPress("del")
      } else if (key === "Escape" || key === "c" || key === "C") {
        handleCalcPress("C")
      } else if (key === "%") {
        e.preventDefault()
        handleCalcPress("%")
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [handleCalcPress])

  return {
    calcDisplay,
    setCalcDisplay,
    calcFormula,
    setCalcFormula,
    calcResetOnNext,
    setCalcResetOnNext,
    calcHistory,
    setCalcHistory,
    showCalcHistory,
    setShowCalcHistory,
    calcFocusedWidgetId,
    setCalcFocusedWidgetId,
    handleCalcPress,
  }
}
